import { useForm, useFieldArray } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { X, Plus, Trash2, FileText, Upload, MapPin, Calendar } from 'lucide-react'
import { Button } from '../common'
import { useQuery } from 'react-query'
import  customerService  from '../../services/customerService'
import { useAuth } from '../../contexts/AuthContextTest'

function InvoiceForm({ 
  isOpen, 
  editingInvoice, 
  onClose, 
  onSubmit, 
  isLoading 
}) {
  const { user } = useAuth()
  const [imagePreview, setImagePreview] = useState(null)
  const [selectedImage, setSelectedImage] = useState(null)

  const { register, handleSubmit, reset, control, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      customerId: '',
      companyId: user?.company?.id || '',
      totalAmount: 0,
      taxPercentage: 0,
      discountPercentage: 0,
      profitPercentage: 0,
      pickUpAddress: '',
      dropOffAddress: '',
      deliveryDate: '',
      paymentStatus: 'Pending',
      orderStatus: 'Pending',
      notes: '',
      image: null,
      isActive: true,
      items: []
    }
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  })

  // Fetch customers
  const { data: customersData } = useQuery('customers', () => customerService.getAll({ limit: 100 }))
  const customers = customersData?.data?.customers || []

  // Watch form values for calculations
  const watchedItems = watch('items')
  const watchedTaxPercentage = watch('taxPercentage')
  const watchedDiscountPercentage = watch('discountPercentage')
  const watchedProfitPercentage = watch('profitPercentage')

  // Reset form when editing invoice changes
  useEffect(() => {
    if (editingInvoice) {
      // Format deliveryDate for date input (YYYY-MM-DD format)
      let formattedDeliveryDate = ''
      if (editingInvoice.deliveryDate) {
        const date = new Date(editingInvoice.deliveryDate)
        if (!isNaN(date.getTime())) {
          formattedDeliveryDate = date.toISOString().split('T')[0]
        } else {
          formattedDeliveryDate = editingInvoice.deliveryDate
        }
      }
      
      // Normalize items to ensure itemName is present and never null
      const normalizedItems = (editingInvoice.items || []).map(item => {
        let itemName = item.itemName ?? item.item_name ?? ''
        if (itemName === null || itemName === undefined) {
          itemName = ''
        } else {
          itemName = String(itemName)
        }
        
        return {
          ...item,
          itemName: itemName, // Always a string, never null
          quantity: item.quantity || 1,
          pricePerQuantity: item.pricePerQuantity || item.price_per_quantity || 0,
          total: item.total || 0
        }
      })

      reset({
        id: editingInvoice.id,
        customerId: editingInvoice.customerId || '',
        companyId: editingInvoice.companyId || user?.company?.id || '',
        totalAmount: editingInvoice.totalAmount || 0,
        taxPercentage: editingInvoice.taxPercentage || 0,
        discountPercentage: editingInvoice.discountPercentage || 0,
        profitPercentage: editingInvoice.profitPercentage || 0,
        pickUpAddress: editingInvoice.pickUpAddress || '',
        dropOffAddress: editingInvoice.dropOffAddress || '',
        deliveryDate: formattedDeliveryDate,
        paymentStatus: editingInvoice.paymentStatus || 'Pending',
        orderStatus: editingInvoice.orderStatus || 'Pending',
        notes: editingInvoice.notes || '',
        image: editingInvoice.image || null,
        isActive: editingInvoice.isActive !== undefined ? editingInvoice.isActive : true,
        items: normalizedItems
      })
      if (editingInvoice.image) {
        setImagePreview(editingInvoice.image)
      }
    } else {
      reset({
        customerId: '',
        companyId: user?.company?.id || '',
        totalAmount: 0,
        taxPercentage: 0,
        discountPercentage: 0,
        profitPercentage: 0,
        pickUpAddress: '',
        dropOffAddress: '',
        deliveryDate: '',
        paymentStatus: 'Pending',
        orderStatus: 'Pending',
        notes: '',
        image: null,
        isActive: true,
        items: []
      })
      setImagePreview(null)
      setSelectedImage(null)
    }
  }, [editingInvoice, reset, user])

  // Calculate total amount from items
  useEffect(() => {
    const subtotal = watchedItems.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 0
      const pricePerQuantity = parseFloat(item.pricePerQuantity) || 0
      return sum + (quantity * pricePerQuantity)
    }, 0)

    // Apply discount
    const discountAmount = subtotal * (parseFloat(watchedDiscountPercentage) || 0) / 100
    const afterDiscount = subtotal - discountAmount

    // Apply tax
    const taxAmount = afterDiscount * (parseFloat(watchedTaxPercentage) || 0) / 100
    const afterTax = afterDiscount + taxAmount

    // Apply profit
    const profitAmount = afterTax * (parseFloat(watchedProfitPercentage) || 0) / 100
    const total = afterTax + profitAmount

    setValue('totalAmount', total.toFixed(2))
  }, [watchedItems, watchedTaxPercentage, watchedDiscountPercentage, watchedProfitPercentage, setValue])

  // Calculate item total
  const calculateItemTotal = (quantity, pricePerQuantity) => {
    return (parseFloat(quantity) || 0) * (parseFloat(pricePerQuantity) || 0)
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setSelectedImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
      setValue('image', file)
    }
  }

  const handleSubmitForm = (data) => {
    // Normalize items to ensure itemName is never null - explicitly handle null/undefined
    const normalizedItems = (data.items || []).map(item => {
      // Explicitly check for null, undefined, or empty string and ensure it's a string
      let itemName = item.itemName ?? item.item_name ?? ''
      // Force convert to string and handle null/undefined explicitly
      if (itemName === null || itemName === undefined) {
        itemName = ''
      } else {
        itemName = String(itemName).trim()
      }
      
      // Build the normalized item, preserving all existing fields (id, invoiceId, etc.)
      const normalizedItem = {
        ...item,
        itemName: itemName, // Always a string, never null - explicitly set
        quantity: parseFloat(item.quantity) || 0,
        pricePerQuantity: parseFloat(item.pricePerQuantity) || item.price_per_quantity || 0,
        total: parseFloat(item.total) || 0
      }
      
      // Remove item_name if it exists (use itemName instead)
      if (normalizedItem.item_name) {
        delete normalizedItem.item_name
      }
      
      return normalizedItem
    })

    // Prepare the data object, ensuring all fields are included
    const submitData = {
      ...data,
      // Ensure deliveryDate is included - send as null if empty string
      deliveryDate: data.deliveryDate && data.deliveryDate.trim() !== '' ? data.deliveryDate : null,
      // Ensure items are properly formatted with itemName (never null)
      items: normalizedItems,
      // Convert numeric fields to proper types
      totalAmount: parseFloat(data.totalAmount) || 0,
      taxPercentage: parseFloat(data.taxPercentage) || 0,
      discountPercentage: parseFloat(data.discountPercentage) || 0,
      profitPercentage: parseFloat(data.profitPercentage) || 0,
      // Include companyId if not present
      companyId: data.companyId || user?.company?.id || null,
      // Ensure boolean fields
      isActive: data.isActive !== undefined ? data.isActive : true,
    }

    // Handle image separately if it's a file
    if (submitData.image && typeof submitData.image === 'object' && submitData.image instanceof File) {
      // Image file will be handled separately if needed
      // For now, remove it from the data object as it should be sent via FormData
      delete submitData.image
    } else if (!submitData.image) {
      // If no image, set to null
      submitData.image = null
    }

    // Final check: ensure no itemName is null in the payload
    submitData.items = submitData.items.map(item => ({
      ...item,
      itemName: item.itemName === null || item.itemName === undefined ? '' : String(item.itemName)
    }))

    console.log('InvoiceForm - Submitting data:', JSON.stringify(submitData, null, 2))
    console.log('InvoiceForm - Items:', submitData.items)
    console.log('InvoiceForm - Items itemName check:', submitData.items.map(item => ({ 
      id: item.id, 
      itemName: item.itemName, 
      type: typeof item.itemName,
      isNull: item.itemName === null,
      isUndefined: item.itemName === undefined
    })))
    console.log('InvoiceForm - deliveryDate value:', submitData.deliveryDate)
    console.log('InvoiceForm - deliveryDate type:', typeof submitData.deliveryDate)
    
    onSubmit(submitData)
  }

  const handleClose = () => {
    reset()
    setImagePreview(null)
    setSelectedImage(null)
    onClose()
  }

  if (!isOpen) return null

  const subtotal = watchedItems.reduce((sum, item) => {
    return sum + calculateItemTotal(item.quantity, item.pricePerQuantity)
  }, 0)
  const discountAmount = subtotal * (parseFloat(watchedDiscountPercentage) || 0) / 100
  const afterDiscount = subtotal - discountAmount
  const taxAmount = afterDiscount * (parseFloat(watchedTaxPercentage) || 0) / 100
  const afterTax = afterDiscount + taxAmount
  const profitAmount = afterTax * (parseFloat(watchedProfitPercentage) || 0) / 100
  const total = afterTax + profitAmount

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity" onClick={handleClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full max-h-[90vh] sm:flex sm:flex-col">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 flex-shrink-0">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                  <FileText className="h-6 w-6 text-primary-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-secondary-900">
                    {editingInvoice ? 'Edit Invoice' : 'Add New Invoice'}
                  </h3>
                  <p className="text-base text-secondary-500">
                    {editingInvoice ? 'Update invoice information' : 'Enter invoice details'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6">
            <form onSubmit={handleSubmit(handleSubmitForm)} className="space-y-6">
              <input type="hidden" {...register('id')} />
              
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="text-base font-medium text-secondary-900 border-b border-secondary-200 pb-2">
                  Basic Information
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Customer *
                    </label>
                    <select
                      {...register('customerId', { required: 'Customer is required' })}
                      className="input"
                    >
                      <option value="">Select Customer</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.id}>
                          {customer.firstName} {customer.lastName} - {customer.phone}
                        </option>
                      ))}
                    </select>
                    {errors.customerId && (
                      <p className="text-danger-600 text-base mt-1">{errors.customerId.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Payment Status
                    </label>
                    <select {...register('paymentStatus')} className="input">
                      <option value="Pending">Pending</option>
                      <option value="Partial">Partial</option>
                      <option value="Paid">Paid</option>
                      <option value="Overdue">Overdue</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Order Status
                    </label>
                    <select {...register('orderStatus')} className="input">
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Delivery Date
                    </label>
                    <input
                      type="date"
                      {...register('deliveryDate')}
                      className="input"
                    />
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="space-y-4">
                <h4 className="text-base font-medium text-secondary-900 border-b border-secondary-200 pb-2 flex items-center">
                  <MapPin className="h-4 w-4 mr-2" />
                  Address Information
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Pick Up Address
                    </label>
                    <textarea
                      {...register('pickUpAddress')}
                      className="input"
                      rows={3}
                      placeholder="Enter pick up address"
                    />
                  </div>

                  <div>
                    <label className="block text-base font-medium text-secondary-700 mb-1">
                      Drop Off Address
                    </label>
                    <textarea
                      {...register('dropOffAddress')}
                      className="input"
                      rows={3}
                      placeholder="Enter drop off address"
                    />
                  </div>
                </div>
              </div>

              {/* Invoice Items */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="text-base font-medium text-secondary-900 border-b border-secondary-200 pb-2">
                    Invoice Items
                  </h4>
                  <Button
                    type="button"
                    onClick={() => append({
                      itemName: '',
                      quantity: 1,
                      pricePerQuantity: 0,
                      total: 0
                    })}
                    variant="primary"
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>

                {fields.length === 0 ? (
                  <div className="text-center py-8 text-secondary-500 border border-dashed border-secondary-300 rounded-lg">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-secondary-300" />
                    <p>No items added yet. Click "Add Item" to start.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {fields.map((field, index) => (
                      <div key={field.id} className="border border-secondary-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                          <div>
                            <label className="block text-base font-medium text-secondary-700 mb-1">
                              Item Name *
                            </label>
                            <input
                              type="text"
                              {...register(`items.${index}.itemName`, { 
                                required: 'Item name is required',
                                setValueAs: (value) => value === null || value === undefined ? '' : String(value)
                              })}
                              className="input"
                              placeholder="Enter item name"
                              defaultValue={field.itemName || ''}
                              onChange={(e) => {
                                const value = e.target.value || ''
                                setValue(`items.${index}.itemName`, value)
                              }}
                            />
                            {errors.items?.[index]?.itemName && (
                              <p className="text-danger-600 text-base mt-1">{errors.items[index].itemName.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-base font-medium text-secondary-700 mb-1">
                              Quantity *
                            </label>
                            <input
                              type="number"
                              min="1"
                              step="0.01"
                              {...register(`items.${index}.quantity`, { 
                                required: 'Quantity is required',
                                min: { value: 0.01, message: 'Quantity must be greater than 0' }
                              })}
                              className="input"
                              onChange={(e) => {
                                const quantity = parseFloat(e.target.value) || 0
                                const pricePerQuantity = parseFloat(watch(`items.${index}.pricePerQuantity`)) || 0
                                setValue(`items.${index}.total`, (quantity * pricePerQuantity).toFixed(2))
                              }}
                            />
                            {errors.items?.[index]?.quantity && (
                              <p className="text-danger-600 text-base mt-1">{errors.items[index].quantity.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-base font-medium text-secondary-700 mb-1">
                              Price Per Quantity *
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              {...register(`items.${index}.pricePerQuantity`, { 
                                required: 'Price is required',
                                min: { value: 0, message: 'Price must be 0 or greater' }
                              })}
                              className="input"
                              onChange={(e) => {
                                const pricePerQuantity = parseFloat(e.target.value) || 0
                                const quantity = parseFloat(watch(`items.${index}.quantity`)) || 0
                                setValue(`items.${index}.total`, (quantity * pricePerQuantity).toFixed(2))
                              }}
                            />
                            {errors.items?.[index]?.pricePerQuantity && (
                              <p className="text-danger-600 text-base mt-1">{errors.items[index].pricePerQuantity.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-base font-medium text-secondary-700 mb-1">
                              Total
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              {...register(`items.${index}.total`)}
                              className="input"
                              readOnly
                            />
                          </div>

                          <div>
                            <Button
                              type="button"
                              onClick={() => remove(index)}
                              variant="danger"
                              size="sm"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pricing & Calculations */}
              <div className="space-y-4">
                <h4 className="text-base font-medium text-secondary-900 border-b border-secondary-200 pb-2">
                  Pricing & Calculations
                </h4>
                
                <div className="bg-secondary-50 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        Subtotal
                      </label>
                      <input
                        type="text"
                        value={subtotal.toFixed(2)}
                        className="input"
                        readOnly
                      />
                    </div>

                    <div>
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        Discount Percentage (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        {...register('discountPercentage')}
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        Discount Amount
                      </label>
                      <input
                        type="text"
                        value={discountAmount.toFixed(2)}
                        className="input"
                        readOnly
                      />
                    </div>

                    <div>
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        Tax Percentage (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        {...register('taxPercentage')}
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        Tax Amount
                      </label>
                      <input
                        type="text"
                        value={taxAmount.toFixed(2)}
                        className="input"
                        readOnly
                      />
                    </div>

                    <div>
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        Profit Percentage (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        {...register('profitPercentage')}
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        Profit Amount
                      </label>
                      <input
                        type="text"
                        value={profitAmount.toFixed(2)}
                        className="input"
                        readOnly
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-base font-medium text-secondary-700 mb-1">
                        Total Amount
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        {...register('totalAmount')}
                        className="input font-semibold text-lg"
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="space-y-4">
                <h4 className="text-base font-medium text-secondary-900 border-b border-secondary-200 pb-2">
                  Additional Information
                </h4>
                
                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    {...register('notes')}
                    className="input"
                    rows={3}
                    placeholder="Enter any additional notes"
                  />
                </div>

                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    Image
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center justify-center px-4 py-2 border border-secondary-300 rounded-lg cursor-pointer hover:bg-secondary-50">
                      <Upload className="h-5 w-5 mr-2" />
                      Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                    {imagePreview && (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="h-20 w-20 object-cover rounded-lg border border-secondary-200"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview(null)
                            setSelectedImage(null)
                            setValue('image', null)
                          }}
                          className="absolute -top-2 -right-2 bg-danger-500 text-white rounded-full p-1 hover:bg-danger-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-base font-medium text-secondary-700 mb-1">
                    Status
                  </label>
                  <select {...register('isActive')} className="input">
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pb-4">
                <Button
                  type="button"
                  onClick={handleClose}
                  variant="outline"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  disabled={isLoading || fields.length === 0}
                >
                  {isLoading ? 'Saving...' : editingInvoice ? 'Update Invoice' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InvoiceForm

